import { Injectable } from '@angular/core';
import {
  Commission,
  FeeResponseModel,
  LinkedCustomer,
} from 'src/app/api/autogenerated';
import { PayoutFeeConfig } from 'src/app/types/PayoutFeeConfig';
import { AffiliateCommission } from '../../../types/AffiliateCommission';
import { AffiliateVendor } from '../../../types/AffiliateVendor';
import { CoreService } from '../../core.service';
import { IAffiliatesService } from '../IAffiliatesService';
import { OpenApiService } from '../open-api.service';

@Injectable({
  providedIn: 'root',
})
export class AffiliatesService implements IAffiliatesService {
  constructor(
    private _coreService: CoreService,
    private _openApiService: OpenApiService,
  ) {}

  async getAffiliateVendors(): Promise<{
    affiliate_code: string;
    affiliate_vendors: AffiliateVendor[];
    is_premium_affiliate_manager: boolean;
  }> {
    try {
      const response = await this._openApiService.getAffiliateVendors();

      if (response.success && response.data) {
        // Transform LinkedCustomer[] from API to AffiliateVendor[]
        const affiliateVendors = this.convertLinkedCustomersToAffiliateVendors(
          response.data.linked_customer || [],
          response.data.is_premium_affiliate_manager || false,
        );

        return {
          affiliate_code: response.data.affiliate_code || '',
          affiliate_vendors: affiliateVendors,
          is_premium_affiliate_manager:
            response.data.is_premium_affiliate_manager || false,
        };
      } else {
        console.error('Error fetching affiliate vendors:', response.error);
        return {
          affiliate_code: '',
          affiliate_vendors: [],
          is_premium_affiliate_manager: false,
        };
      }
    } catch (error) {
      console.error('Error in getAffiliateVendors:', error);
      return {
        affiliate_code: '',
        affiliate_vendors: [],
        is_premium_affiliate_manager: false,
      };
    }
  }

  async updatePayoutFeeConfig(
    affiliateVendorId: string,
    payoutFeeConfig: PayoutFeeConfig,
  ): Promise<void> {
    try {
      const response = await this._openApiService.updatePayoutFeeConfig(
        affiliateVendorId,
        payoutFeeConfig,
      );

      if (!response.success) {
        console.error('Error updating payout fee config:', response.error);
        throw new Error(response.error || 'Failed to update payout fee config');
      }
    } catch (error) {
      console.error('Error in updatePayoutFeeConfig:', error);
      throw error;
    }
  }

  /**
   * Convert LinkedCustomer data from API to AffiliateVendor format
   */
  private convertLinkedCustomersToAffiliateVendors(
    linkedCustomers: LinkedCustomer[],
    isPremiumAffiliateManager: boolean,
  ): AffiliateVendor[] {
    return linkedCustomers.map((customer) =>
      this.convertLinkedCustomerToAffiliateVendor(
        customer,
        isPremiumAffiliateManager,
      ),
    );
  }

  /**
   * Convert a single LinkedCustomer to AffiliateVendor
   */
  private convertLinkedCustomerToAffiliateVendor(
    linkedCustomer: LinkedCustomer,
    isPremiumAffiliateManager: boolean,
  ): AffiliateVendor {
    // Extract fee information and convert to PayoutFeeConfig
    const payoutFeeConfig: PayoutFeeConfig = this.convertFeeToPayoutFeeConfig(
      linkedCustomer.fee,
    );

    // Convert API status to AffiliateVendor status
    const convertStatus = (apiStatus: string) => {
      switch (apiStatus) {
        case 'PENDING':
          return 'kyc_in_progress';
        case 'IN_PROGRESS':
          return 'kyc_in_progress';
        case 'APPROVED':
          return 'verified';
        case 'SIGNED_UP':
          return 'signed_up';
        case 'UNDER_REVIEW':
          return 'under_verification';
        default:
          return 'signed_up';
      }
    };

    return {
      id: linkedCustomer.customer_id,
      name: linkedCustomer.registered_name,
      email: linkedCustomer.registered_email,
      verifiedAt: linkedCustomer.verified_at,
      logo: '', // API doesn't provide logo, setting empty string as default
      status: convertStatus(linkedCustomer.verification_status),
      payoutFeeConfig: payoutFeeConfig,
      isPremiumAffiliateManager: isPremiumAffiliateManager,
    };
  }

  /**
   * Convert API fee data to PayoutFeeConfig
   */
  private convertFeeToPayoutFeeConfig(fee: FeeResponseModel): PayoutFeeConfig {
    const fxRate = fee?.fx_rate || 0;
    const remitCircleSpreadPaise = 40;
    const clientNewSpreadPaise = fxRate;
    const clientSharePaise = clientNewSpreadPaise - remitCircleSpreadPaise;
    const extraPaise = clientSharePaise / 0.5;
    const bankSpreadPaise = remitCircleSpreadPaise + extraPaise;

    return {
      sourceCurrency: fee.sell_currency || 'USD',
      destinationCurrency: fee.buy_currency || 'INR',
      fixedPayoutFees: fee?.fixed_fee || 0,
      variablePayoutFeesPercentage: fee?.variable_fee_percentage || 0,
      minimumPayoutAmount: fee?.minimum_amount || 0,
      bankRate: Math.round(bankSpreadPaise * 100) / 100,
      fxRate,
    };
  }

  async getAffiliateCommissions(): Promise<{
    total_commission_earned_amount: number;
    affiliate_commissions: AffiliateCommission[];
  }> {
    try {
      console.log('Calling getAffiliateCommissions API...');
      const response = await this._openApiService.getAffiliateCommissions();
      console.log('API Response:', response);

      if (response.success && response.data) {
        const convertedCommissions = this.convertCommissionsFromApiToFronEnd(
          response.data.commissions || [],
        );
        console.log('Converted commissions:', convertedCommissions);

        return {
          total_commission_earned_amount:
            response.data.total_commission_earned || 0,
          affiliate_commissions: convertedCommissions,
        };
      } else {
        console.error('Error fetching affiliate commissions: ', response.error);
        return { total_commission_earned_amount: 0, affiliate_commissions: [] };
      }
    } catch (error) {
      console.error('Error in getAffiliateCommissions: ', error);
      return { total_commission_earned_amount: 0, affiliate_commissions: [] };
    }
  }

  private convertCommissionsFromApiToFronEnd(
    affiliateCommissions: Commission[],
  ): AffiliateCommission[] {
    return affiliateCommissions.map((commission) =>
      this.convertCommissionToAffiliateCommission(commission),
    );
  }

  private convertCommissionToAffiliateCommission(
    commission: Commission,
  ): AffiliateCommission {
    const convertedCommission: AffiliateCommission = {
      commissionEarnedDate: commission.payout_date,
      payoutReference: commission.payout_reference,
      vendor: commission.client,
      email: commission.email,
      grossAmount: commission.gross_amount,
      netAmount: commission.net_amount,
      settledAmount: commission.settled_amount,
      commissionEarnedAmount: commission.commission_earned,
    };

    console.log('Converting commission:', commission);
    console.log('Converted commission:', convertedCommission);

    return convertedCommission;
  }
}
